<template>
  <div class="share-dialog-content pb-5 mr-md-5">
    <v-alert color="var(--red-100)" v-if="isRequestError" class="mb-7">
      <div class="d-flex flex-no-wrap error-message">
        <div class="mr-3">
          <v-icon color="var(--red-500)">mdi-alert-circle</v-icon>
        </div>
        <div>
          <span class="font-weight-bold">Sorry!</span> The details you have
          entered below are invalid. Please check your details and try again.
        </div>
      </div>
    </v-alert>
    <v-alert
      v-if="isEmailTimeoutError"
      color="var(--orange-100)"
      class="mt-4 mb-0 note-warning"
    >
      <div class="d-flex flex-no-wrap">
        <div class="mr-3">
          <v-icon color="var(--orange-500)">icon-warning-small</v-icon>
        </div>
        <div>
          <span class="font-weight-bold">Warning!</span>&nbsp;Sending the email
          is taking longer than usual but is expected to complete soon. If the
          email does not arrive with the recipient within the next 10 minutes,
          please try again.
        </div>
      </div>
    </v-alert>
    <h4 class="text-h7 font-weight-bold mt-4 mb-1">
      Select which document(s) you want to send
    </h4>
    <ValidationObserver v-slot="{ passes }">
      <form @submit.prevent="passes(onSubmit)">
        <div class="d-flex flex-column pl-3">
          <BaseCheckBox
            :checked="true"
            label="Quote summary"
            name="Quote Summary"
            :readonly="true"
            aria-label="Quote Summary"
          />
          <BaseCheckBox
            @input="onChange"
            name="policyBooklet"
            label="Policy Booklet"
            aria-label="Policy Booklet"
          />
          <BaseCheckBox
            @input="onChange"
            name="insuranceProductInfo"
            label="Insurance Product Information Document"
            aria-label="Insurance Product Information Document"
          />
          <BaseCheckBox
            @input="onChange"
            name="multiQuoteSummary"
            label="All Quotes Summary"
            v-if="isComplete && showAllQuoteSummary"
            aria-label="All Quotes Summary"
          />
          <BaseCheckBox
            @input="onChange"
            name="quoteAndApplicationDetails"
            label="Quote and Application Summary"
            v-if="isComplete"
            aria-label="Quote and Application Summary"
          />
          <BaseCheckBox
            @input="onChange"
            name="suitabilityReport"
            label="Suitability Report"
            v-if="isComplete && showSuitabilityReport"
            aria-label="Suitability Report"
          />
          <BaseCheckBox
            @input="onChange"
            name="confirmationOfCover"
            label="Confirmation of Cover"
            v-if="isComplete && showConfirmationOfCover"
            aria-label="Confirmation of Cover"
          />
          <BaseCheckBox
            @input="onChange"
            name="statementOfDemandsAndNeeds"
            label="Statement of Demands and Needs"
            v-if="
              (isComplete && showSdn) || callChannel === CALL_CHANNEL.intrinsic
            "
            aria-label="Statement of Demands and Needs"
          />
        </div>
        <h4 class="mt-6">Client details</h4>
        <div class="col-12 col-md-8 pa-0">
          <BaseInput
            :rules="{
              required: { required: true, fieldLabel: 'Client email' },
              email: true,
            }"
            :hide-details="false"
            vid="client-email-address"
            label="Client email address"
            name="Client email address"
            fieldName="clientEmailAddress"
            class="mt-2 mb-3"
            :dense="$vuetify.breakpoint.lgAndUp"
            @input="onChange"
            aria-label="Client email address"
          />
        </div>
        <h4>Your details</h4>
        <div class="col-12 col-md-8 pa-0">
          <BaseInput
            :rules="{
              required: { required: true, fieldLabel: 'Email' },
              email: true,
            }"
            :hide-details="false"
            vid="your-email-address"
            label="Your email address"
            name="Your email address"
            fieldName="yourEmailAddress"
            class="mt-2"
            :dense="$vuetify.breakpoint.lgAndUp"
            @input="onChange"
            aria-label="Your email address"
          />
        </div>
        <div class="col-12 col-md-8 pa-0">
          <BaseInput
            :rules="{ phone_number: { fieldLabel: 'Your telephone number' } }"
            :hide-details="false"
            vid="your-phone"
            label="Your telephone number"
            name="Your telephone number"
            fieldName="telephoneNumber"
            hideValidationIcon
            :dense="$vuetify.breakpoint.lgAndUp"
            @input="onChange"
            aria-label="Your telephone number"
          />
        </div>
        <div class="d-flex justify-center">
          <BaseButton
            text="SEND EMAIL"
            loadingText="SENDING..."
            depressed
            rounded
            :minWidth="$vuetify.breakpoint.lgAndUp ? '420' : '250'"
            height="60"
            :color="'psorange'"
            class="font-weight-bold py-6 px-10 font-20 mt-6"
            type="submit"
            :loading="loading"
            aria-label="Send email"
          />
        </div>
      </form>
    </ValidationObserver>
    <v-dialog v-model="isShowSuccess" max-width="556">
      <v-card
        class="d-flex flex-column justify-center align-center"
        min-height="367"
      >
        <v-btn
          text
          icon
          class="btn-close"
          aria-label="Close"
          @click="isShowSuccess = false"
        >
          <v-icon> mdi-close </v-icon>
        </v-btn>
        <v-icon class="font-60" color="#178748">mdi-check-circle</v-icon>
        <span class="font-20 font-weight-bold">Success!</span>
        <span>The email has been sent successfully.</span>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import BaseButton from "@/components/BaseComponents/BaseButton.vue";
import BaseCheckBox from "@/components/BaseComponents/BaseCheckBox.vue";
import BaseInput from "@/components/BaseComponents/BaseInput.vue";
import { CALL_CHANNEL, MONTHLY } from "@/constants/common.constants";
import QuoteService from "@/services/quote.service";
import {
  APPLICATION_MODULE,
  QUESTIONS_MODULE,
} from "@/store/types/module-types";
import { ValidationObserver } from "vee-validate";
import { mapGetters, mapState } from "vuex";

export default {
  components: {
    BaseInput,
    ValidationObserver,
    BaseButton,
    BaseCheckBox,
  },
  props: {
    content: [Array, Object],
    quoteId: String,
    quoteRequestId: Number,
    showSdn: {
      type: Boolean,
      default: false,
    },
    showSuitabilityReport: {
      type: Boolean,
      default: false,
    },
    showAllQuoteSummary: {
      type: Boolean,
      default: true,
    },
    showConfirmationOfCover: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      form: {
        quoteSummary: true,
        policyBooklet: false,
        insuranceProductInfo: false,
        quoteAndApplicationDetails: false,
        multiQuoteSummary: false,
        statementOfDemandsAndNeeds: false,
        suitabilityReport: false,
        confirmationOfCover: false,
        telephoneNumber: null,
        clientEmailAddress: null,
        yourEmailAddress: null,
      },
      isRequestError: false,
      isEmailTimeoutError: false,
      loading: false,
      isShowSuccess: false,
      CALL_CHANNEL,
    };
  },
  computed: {
    ...mapGetters(QUESTIONS_MODULE, [
      "httpConfigAsRqs",
      "callChannel",
      "getAnswer",
    ]),
    ...mapState(APPLICATION_MODULE, {
      questionMap: "questionMap",
    }),
    isComplete() {
      return (
        this.$route.name === "Complete" || this.$route.meta.isComplete === true
      );
    },
    sharePayload() {
      return {
        ToAddress: this.form.clientEmailAddress,
        SenderAddress: this.form.yourEmailAddress,
        SenderTel: this.form.telephoneNumber,
        Criteria: {
          PaymentFrequency:
            this.questionMap.PaymentFrequency?.Value ||
            this.getAnswer("PaymentFrequency") || // for when retrieving data back and the answer has the source of default
            MONTHLY,
          ContentsAD: this.buildingsOnlyType
            ? undefined
            : this.content.contentsAd,
          ContentsCoverLevel: this.buildingsOnlyType
            ? undefined
            : this.content.contentsCover?.amount,
          BuildingsAD: this.contentsOnlyType
            ? undefined
            : this.content.buildingsAd,
          BuildingsCoverLevel: this.contentsOnlyType
            ? undefined
            : this.content.buildingsCover?.amount,
        },
      };
    },
    contentsOnlyType() {
      return (
        this.content.type === "Contents only" ||
        this.content.type === "Contents"
      );
    },
    buildingsOnlyType() {
      return (
        this.content.type === "Buildings only" ||
        this.content.type === "Buildings"
      );
    },
    shareQuotePayload() {
      const docs = ["quote"];
      if (this.form.policyBooklet) docs.push("terms");

      if (this.form.insuranceProductInfo) docs.push("ipid");

      if (this.form.quoteAndApplicationDetails) docs.push("qas");

      if (this.form.multiQuoteSummary) docs.push("multiquote");

      if (this.form.suitabilityReport) docs.push("suitability");

      if (this.form.confirmationOfCover) docs.push("cofc");

      if (this.form.statementOfDemandsAndNeeds) {
        if (this.callChannel === CALL_CHANNEL.intrinsic) {
          docs.push("factfindsdn");
        } else {
          docs.push("sdn");
        }
      }
      const payload = this.sharePayload;
      payload.Documents = docs;
      return payload;
    },
    shareResultsPayload() {
      const docs = ["multiquote"];
      if (this.form.policyBooklet) docs.push("terms");

      if (this.form.insuranceProductInfo) docs.push("ipid");

      if (this.form.quoteAndApplicationDetails) docs.push("qas");

      if (this.form.suitabilityReport) docs.push("suitability");

      if (this.form.confirmationOfCover) docs.push("cofc");

      if (this.form.statementOfDemandsAndNeeds) {
        if (this.callChannel === CALL_CHANNEL.intrinsic) {
          docs.push("factfindsdn");
        } else {
          docs.push("sdn");
        }
      }
      const payload = this.sharePayload;
      payload.Documents = docs;
      return payload;
    },
  },
  methods: {
    onSubmit() {
      this.loading = true;
      this.isRequestError = false;
      this.isEmailTimeoutError = false;
      if (this.quoteId) {
        this.shareQuote();
      } else if (this.quoteRequestId) {
        this.shareResults();
      }
    },
    shareQuote() {
      QuoteService.shareQuote(
        this.quoteId,
        this.shareQuotePayload,
        this.httpConfigAsRqs
      )
        .then(() => {
          this.loading = false;
          this.$emit("success");
          this.isShowSuccess = true;
        })
        .catch((error) => {
          if (error.response.status === 504) {
            this.isEmailTimeoutError = true;
          } else {
            this.isRequestError = true;
          }
          this.loading = false;
          setTimeout(() => {
            let elm = document.querySelector(".error-message");
            if (elm) {
              elm.scrollIntoView({
                behavior: "smooth",
                block: "end",
                inline: "nearest",
              });
            }
          }, 300);
        });
    },
    shareResults() {
      QuoteService.shareResults(
        this.quoteRequestId,
        this.shareResultsPayload,
        this.httpConfigAsRqs
      )
        .then(() => {
          this.loading = false;
          this.$emit("success");
          this.isShowSuccess = true;
        })
        .catch(() => {
          this.loading = false;
          this.isRequestError = true;
          setTimeout(() => {
            let elm = document.querySelector(".error-message");
            if (elm) {
              elm.scrollIntoView({
                behavior: "smooth",
                block: "end",
                inline: "nearest",
              });
            }
          }, 300);
        });
    },
    onChange(val, name) {
      this.form[name] = val;
    },
  },
};
</script>

<style lang="scss" scoped>
.v-dialog {
  position: relative;

  .btn-close {
    position: absolute;
    top: 20px;
    right: 20px;
    font-size: 30px;
    color: $black-2;

    .mdi-close {
      font-size: 32px;
    }
  }

  .share-dialog-content {
    font-size: 16px;
  }
}
:deep(.v-input) {
  margin-top: 4px;
}
</style>
